import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Droplets,
  Fuel,
  Gauge,
  RotateCcw,
  Route,
  Search,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import { fetchAllCarRentals } from '../services/carRentalsService';
import {
  computeVehicleReportDerived,
  createVehicleReportGridRow,
  deleteVehicleReportGridRow,
  fetchAllVehicleReportGridRows,
  getVehicleReportGridErrorMessage,
  readVehicleReportGridSessionCache,
  upsertVehicleReportGridRow,
  writeVehicleReportGridSessionCache,
} from '../services/vehicleReportGridService';
import './VehicleReport.css';

const ITEMS_PER_PAGE = 10;
const YEAR_OPTIONS = [2024, 2025, 2026, 2027, 2028];
const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];
const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const VR_TABLE_HEADERS = [
  'Project',
  'User name',
  'Reg number',
  'Rental company',
  'Previous odometer (KM)',
  'Current odometer (KM)',
  'KM traveled',
  'Fuel used (L)',
  'Fuel cost by card',
  'Fuel cost by cash',
  'Total fuel cost',
  'Rental days',
  'Price per month',
  'KM/L',
  'L/100 km'
];

const formatAmount = (n) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatNumber = (n, decimals = 2) =>
  Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

const parseAmountInput = (raw) => {
  const cleaned = String(raw ?? '').replace(/,/g, '').trim();
  if (cleaned === '') return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const parseIntInput = (raw) => {
  const cleaned = String(raw ?? '').replace(/,/g, '').trim();
  if (cleaned === '') return 0;
  const n = parseInt(cleaned, 10);
  return Number.isFinite(n) ? n : 0;
};

const periodCacheKey = (year, month) => `${year}-${month}`;

const filterRowsByPeriod = (allRows, year, month) =>
  allRows.filter((r) => r.year === year && r.month === month);

const sortPeriodRows = (rows) =>
  [...rows].sort((a, b) => {
    const p = String(a.project || '').localeCompare(String(b.project || ''), undefined, {
      sensitivity: 'base'
    });
    if (p !== 0) return p;
    return String(a.regNumber || '').localeCompare(String(b.regNumber || ''), undefined, {
      sensitivity: 'base'
    });
  });

const buildPeriodCache = (allRows) => {
  const map = new Map();
  allRows.forEach((row) => {
    const key = periodCacheKey(row.year, row.month);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });
  map.forEach((rows, key) => map.set(key, sortPeriodRows(rows)));
  return map;
};

const normalizeRegNumber = (value) => String(value ?? '').trim().toUpperCase();

/** Last month current odometer for the same reg — seeds previous odometer. */
const findPriorMonthCurrentOdometer = (allRows, year, month, regNumber) => {
  const reg = normalizeRegNumber(regNumber);
  if (!reg) return 0;
  const currentPeriod = year * 12 + month;
  const prior = allRows
    .filter((r) => {
      if (normalizeRegNumber(r.regNumber) !== reg) return false;
      return (r.year || 0) * 12 + (r.month || 0) < currentPeriod;
    })
    .sort((a, b) => {
      const pa = (a.year || 0) * 12 + (a.month || 0);
      const pb = (b.year || 0) * 12 + (b.month || 0);
      return pb - pa;
    });
  return Number(prior[0]?.currentOdometerKm) || 0;
};

/** Match vehicle report row ↔ car rental row (reg first, else project/user/company). */
const carRentalMatchKey = (row) => {
  const reg = normalizeRegNumber(row?.regNumber);
  if (reg) return `reg:${reg}`;
  const meta = [
    String(row?.project ?? '').trim(),
    String(row?.userName ?? '').trim(),
    String(row?.rentalCompany ?? '').trim()
  ]
    .join('|')
    .toLowerCase();
  if (meta !== '||') return `meta:${meta}`;
  return null;
};

/** True if this vehicle-report row should remain for the month after car-rental sync. */
const shouldKeepVehicleReportRowForPeriod = (vrRow, carRows) => {
  const key = carRentalMatchKey(vrRow);
  if (!key) return false;

  if (carRows.some((car) => carRentalMatchKey(car) === key)) return true;

  if (key.startsWith('meta:')) {
    const hasCarWithRegForSameMeta = carRows.some((car) => {
      const reg = normalizeRegNumber(car.regNumber);
      if (!reg) return false;
      const carMeta = [
        String(car.project ?? '').trim(),
        String(car.userName ?? '').trim(),
        String(car.rentalCompany ?? '').trim()
      ]
        .join('|')
        .toLowerCase();
      return `meta:${carMeta}` === key;
    });
    return !hasCarWithRegForSameMeta;
  }

  return false;
};

const CAR_RENTAL_SYNC_FIELDS = ['project', 'userName', 'rentalCompany'];

const isDuplicateRegInRows = (allRows, rowId, regNumber) => {
  const key = normalizeRegNumber(regNumber);
  if (!key) return false;
  return allRows.some((r) => r.id !== rowId && normalizeRegNumber(r.regNumber) === key);
};

const withDerived = (row) => ({
  ...row,
  ...computeVehicleReportDerived(row)
});

const rowMatchesSearch = (row, query) => {
  if (!query.trim()) return true;
  const q = query.toLowerCase().trim();
  const haystack = [
    row.project,
    row.userName,
    row.regNumber,
    row.rentalCompany,
    row.previousOdometerKm,
    row.currentOdometerKm,
    row.kmMoved,
    row.fuelUsedLiters,
    row.rentalDays,
    row.pricePerMonth
  ]
    .map((v) => String(v ?? '').toLowerCase())
    .join(' ');
  return haystack.includes(q);
};

const computeVehicleReportStats = (dataRows) => {
  let totalKm = 0;
  let totalFuelL = 0;
  let totalFuelCost = 0;
  let kmPerLiterSum = 0;
  let kmPerLiterCount = 0;

  dataRows.forEach((r) => {
    totalKm += Number(r.kmMoved) || 0;
    totalFuelL += Number(r.fuelUsedLiters) || 0;
    totalFuelCost += Number(r.totalFuelCost) || 0;
    if (r.fuelKmPerLiter > 0) {
      kmPerLiterSum += r.fuelKmPerLiter;
      kmPerLiterCount += 1;
    }
  });

  return {
    totalKm,
    totalFuelL,
    totalFuelCost,
    avgKmPerLiter: kmPerLiterCount ? kmPerLiterSum / kmPerLiterCount : 0,
  };
};

function VrPageSkeleton() {
  return (
    <div className="vr-skeleton" aria-busy="true" aria-label="Loading vehicle report">
      <section className="vr-intro" aria-hidden>
        <div className="vr-intro-text">
          <Skeleton className="h-7 w-48 max-w-full" />
          <Skeleton className="mt-2.5 h-4 w-96 max-w-full" />
        </div>
        <div className="vr-intro-actions">
          <Skeleton className="h-9 w-24 rounded-lg" />
          <Skeleton className="h-9 w-28 rounded-lg" />
          <Skeleton className="h-9 w-9 rounded-lg" />
        </div>
      </section>
      <div className="vr-summary-cards" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="vr-summary-card vr-summary-card--skel">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <Skeleton className="mt-3 h-4 w-24" />
            <Skeleton className="mt-2 h-6 w-28" />
          </div>
        ))}
      </div>
      <div className="vr-toolbar" aria-hidden>
        <Skeleton className="h-9 w-full max-w-md rounded-lg" />
        <Skeleton className="h-9 w-28 rounded-lg" />
        <Skeleton className="h-9 w-28 rounded-lg" />
      </div>
      <div className="vr-table-shell" aria-hidden>
        <div className="vr-skel-rows">
          {Array.from({ length: 6 }, (_, r) => (
            <Skeleton key={r} className="h-10 w-full rounded-md" />
          ))}
        </div>
      </div>
    </div>
  );
}

function VehicleReport() {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [filterProject, setFilterProject] = useState('All');
  const [filterCompany, setFilterCompany] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [amountFocusId, setAmountFocusId] = useState(null);
  const [hoverRow, setHoverRow] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = ITEMS_PER_PAGE;

  const saveTimers = useRef({});
  const rowSnapshotsRef = useRef({});
  const rowsLoadedRef = useRef(false);
  const rowsRef = useRef([]);
  const allRowsRef = useRef([]);
  const carRentalsRef = useRef([]);
  const periodCacheRef = useRef(new Map());
  const hasLoadedOnceRef = useRef(false);
  const syncingRef = useRef(false);
  const selectedYearRef = useRef(selectedYear);
  const selectedMonthRef = useRef(selectedMonth);

  useEffect(() => {
    selectedYearRef.current = selectedYear;
  }, [selectedYear]);

  useEffect(() => {
    selectedMonthRef.current = selectedMonth;
  }, [selectedMonth]);

  const syncAllRowsStore = useCallback((allRows) => {
    allRowsRef.current = allRows;
    periodCacheRef.current = buildPeriodCache(allRows);
    writeVehicleReportGridSessionCache(allRows);
  }, []);

  const syncPeriodInStore = useCallback((periodRows) => {
    const y = selectedYearRef.current;
    const m = selectedMonthRef.current;
    const key = periodCacheKey(y, m);
    const sorted = sortPeriodRows(periodRows.map(withDerived));
    periodCacheRef.current.set(key, sorted);
    const others = allRowsRef.current.filter((r) => r.year !== y || r.month !== m);
    const all = [...others, ...sorted];
    allRowsRef.current = all;
    writeVehicleReportGridSessionCache(all);
  }, []);

  const applyRowsToState = useCallback((data) => {
    const derivedRows = data.map(withDerived);
    const snapshots = {};
    derivedRows.forEach((row) => {
      snapshots[row.id] = row;
    });
    rowSnapshotsRef.current = snapshots;
    setRows(derivedRows);
    rowsRef.current = derivedRows;
    rowsLoadedRef.current = true;
  }, []);

  const applySelectedPeriod = useCallback(() => {
    const y = selectedYearRef.current;
    const m = selectedMonthRef.current;
    const key = periodCacheKey(y, m);
    const periodRows =
      periodCacheRef.current.get(key) ?? filterRowsByPeriod(allRowsRef.current, y, m);
    applyRowsToState(periodRows);
  }, [applyRowsToState]);

  const clearPendingSaves = useCallback(() => {
    Object.values(saveTimers.current).forEach(clearTimeout);
    saveTimers.current = {};
  }, []);

  /** Create/update rows from Car Rental Invoicing for the selected month (first 4 columns). */
  const syncPeriodFromCarRental = useCallback(
    async (year, month) => {
      if (syncingRef.current) return;
      const carRows = carRentalsRef.current.filter((r) => r.year === year && r.month === month);
      if (carRows.length === 0) {
        const periodRows = filterRowsByPeriod(allRowsRef.current, year, month);
        if (periodRows.length > 0) {
          syncingRef.current = true;
          setSyncing(true);
          try {
            for (const row of periodRows) {
              await deleteVehicleReportGridRow(row.id);
              delete rowSnapshotsRef.current[row.id];
            }
            const others = allRowsRef.current.filter((r) => r.year !== year || r.month !== month);
            syncAllRowsStore(others);
    } finally {
            syncingRef.current = false;
            setSyncing(false);
          }
        }
        applyRowsToState([]);
        return;
      }

      syncingRef.current = true;
      setSyncing(true);
      try {
        const periodRows = filterRowsByPeriod(allRowsRef.current, year, month);
        const existingByKey = new Map();
        periodRows.forEach((r) => {
          const key = carRentalMatchKey(r);
          if (key) existingByKey.set(key, r);
        });

        let nextPeriodRows = [...periodRows];
        let changed = false;

        for (const src of carRows) {
          const key = carRentalMatchKey(src);
          if (!key) continue;

          const patch = {
            project: src.project ?? '',
            userName: src.userName ?? '',
            regNumber: src.regNumber ?? '',
            rentalCompany: src.rentalCompany ?? ''
          };

          const existing = existingByKey.get(key);
          if (existing) {
            const differs = CAR_RENTAL_SYNC_FIELDS.some(
              (f) => String(existing[f] ?? '').trim() !== String(patch[f] ?? '').trim()
            );
            if (differs) {
              const saved = await upsertVehicleReportGridRow(existing.id, { ...existing, ...patch });
              const merged = withDerived(saved);
              nextPeriodRows = nextPeriodRows.map((r) => (r.id === existing.id ? merged : r));
              rowSnapshotsRef.current[existing.id] = merged;
              changed = true;
            }
          } else {
            const created = await createVehicleReportGridRow({
              ...patch,
              previousOdometerKm: findPriorMonthCurrentOdometer(
                allRowsRef.current,
                year,
                month,
                src.regNumber
              ),
              currentOdometerKm: 0,
              fuelUsedLiters: 0,
              fuelCostCard: 0,
              fuelCostCash: 0,
              rentalDays: 0,
              pricePerMonth: 0,
              month,
              year
            });
            const row = withDerived(created);
            nextPeriodRows.push(row);
            rowSnapshotsRef.current[row.id] = row;
            existingByKey.set(key, row);
            changed = true;
          }
        }

        const staleRows = nextPeriodRows.filter(
          (r) => !shouldKeepVehicleReportRowForPeriod(r, carRows)
        );
        if (staleRows.length > 0) {
          for (const row of staleRows) {
            await deleteVehicleReportGridRow(row.id);
            delete rowSnapshotsRef.current[row.id];
          }
          nextPeriodRows = nextPeriodRows.filter((r) =>
            shouldKeepVehicleReportRowForPeriod(r, carRows)
          );
          changed = true;
        }

        if (changed) {
          syncPeriodInStore(nextPeriodRows);
        }
        applyRowsToState(nextPeriodRows);
    } catch (err) {
      console.error(err);
        setError(`Failed to sync from Car Rental: ${getVehicleReportGridErrorMessage(err)}`);
        applySelectedPeriod();
    } finally {
        syncingRef.current = false;
        setSyncing(false);
      }
    },
    [applyRowsToState, applySelectedPeriod, syncAllRowsStore, syncPeriodInStore]
  );

  const loadAllRows = useCallback(async () => {
    setError(null);
    clearPendingSaves();

    const sessionCached = readVehicleReportGridSessionCache();
    if (sessionCached?.length) {
      syncAllRowsStore(sessionCached);
      applySelectedPeriod();
      setLoading(false);
      setRefreshing(true);
    } else if (!hasLoadedOnceRef.current) {
      rowSnapshotsRef.current = {};
      rowsLoadedRef.current = false;
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const gridRows = await fetchAllVehicleReportGridRows();
      syncAllRowsStore(gridRows);

      try {
        const carRentals = await fetchAllCarRentals();
        carRentalsRef.current = carRentals;
        await syncPeriodFromCarRental(selectedYearRef.current, selectedMonthRef.current);
      } catch (carErr) {
        console.warn('Car rental sync failed:', carErr);
        applySelectedPeriod();
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      if (!sessionCached?.length) {
        setError(
          `Failed to load vehicle reports: ${getVehicleReportGridErrorMessage(err)}. Run npm run setup:vehicle-report-grid.`
        );
        setRows([]);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      hasLoadedOnceRef.current = true;
    }
  }, [clearPendingSaves, syncAllRowsStore, applySelectedPeriod, syncPeriodFromCarRental]);

  useEffect(() => {
    loadAllRows();
  }, [loadAllRows]);

  useEffect(() => {
    if (!hasLoadedOnceRef.current) return;
    setError(null);
    clearPendingSaves();
    applySelectedPeriod();
    if (carRentalsRef.current.length) {
      syncPeriodFromCarRental(selectedYear, selectedMonth);
    }
  }, [selectedYear, selectedMonth, clearPendingSaves, applySelectedPeriod, syncPeriodFromCarRental]);

  useEffect(() => clearPendingSaves, [clearPendingSaves]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const duplicateRegRowIds = useMemo(() => {
    const byReg = new Map();
    rows.forEach((r) => {
      const key = normalizeRegNumber(r.regNumber);
      if (!key) return;
      if (!byReg.has(key)) byReg.set(key, []);
      byReg.get(key).push(r.id);
    });
    const dupIds = new Set();
    byReg.forEach((ids) => {
      if (ids.length > 1) ids.forEach((id) => dupIds.add(id));
    });
    return dupIds;
  }, [rows]);

  const duplicateRegCount = useMemo(() => {
    const keys = new Set();
    const byReg = new Map();
    rows.forEach((r) => {
      const key = normalizeRegNumber(r.regNumber);
      if (!key) return;
      if (!byReg.has(key)) byReg.set(key, 0);
      byReg.set(key, byReg.get(key) + 1);
    });
    byReg.forEach((count, key) => {
      if (count > 1) keys.add(key);
    });
    return keys.size;
  }, [rows]);

  const projectOptions = useMemo(() => {
    const set = new Set(['All']);
    rows.forEach((r) => {
      const p = String(r.project || '').trim();
      if (p) set.add(p);
    });
    return Array.from(set).sort((a, b) => (a === 'All' ? -1 : a.localeCompare(b)));
  }, [rows]);

  const companyOptions = useMemo(() => {
    const set = new Set(['All']);
    rows.forEach((r) => {
      const c = String(r.rentalCompany || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => (a === 'All' ? -1 : a.localeCompare(b)));
  }, [rows]);

  const filtersActive =
    filterProject !== 'All' || filterCompany !== 'All' || searchQuery.trim() !== '';

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterProject !== 'All' && r.project !== filterProject) return false;
      if (filterCompany !== 'All' && r.rentalCompany !== filterCompany) return false;
      if (!rowMatchesSearch(r, searchQuery)) return false;
      return true;
    });
  }, [rows, filterProject, filterCompany, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedMonth, filterProject, filterCompany, searchQuery]);

  const totalFiltered = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = totalFiltered > 0 ? (safePage - 1) * itemsPerPage + 1 : 0;
  const indexOfLastItem = Math.min(safePage * itemsPerPage, totalFiltered);
  const paginatedRows = useMemo(
    () => filteredRows.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage),
    [filteredRows, safePage, itemsPerPage]
  );

  const goToPage = (page) => {
    setCurrentPage(page);
    const tableWrapper = document.querySelector('.vr-table-wrapper');
    if (tableWrapper) {
      tableWrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const goToPreviousPage = () => {
    if (safePage > 1) goToPage(safePage - 1);
  };

  const goToNextPage = () => {
    if (safePage < totalPages) goToPage(safePage + 1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else if (safePage <= 3) {
      for (let i = 1; i <= 4; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    } else if (safePage >= totalPages - 2) {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = safePage - 1; i <= safePage + 1; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const stats = useMemo(() => computeVehicleReportStats(filteredRows), [filteredRows]);

  const scheduleSave = useCallback(
    (rowId) => {
      if (!rowId || !rowsLoadedRef.current) return;
      if (saveTimers.current[rowId]) clearTimeout(saveTimers.current[rowId]);
      saveTimers.current[rowId] = setTimeout(async () => {
        delete saveTimers.current[rowId];
        const snapshot = rowSnapshotsRef.current[rowId];
        if (!snapshot?.id) return;
        if (isDuplicateRegInRows(rowsRef.current, rowId, snapshot.regNumber)) {
          setError('Duplicate registration number in this month.');
      return;
    }
    try {
          const saved = await upsertVehicleReportGridRow(rowId, snapshot);
          const live = rowSnapshotsRef.current[rowId];
          const merged = withDerived(live ? { ...saved, ...live, id: rowId } : saved);
          rowSnapshotsRef.current[rowId] = merged;
          setRows((prev) => {
            const next = prev.map((r) => (r.id === rowId ? merged : r));
            rowsRef.current = next;
            syncPeriodInStore(next);
            return next;
          });
          setError((prev) =>
            prev && (prev.startsWith('Failed to save') || prev.startsWith('Failed to add'))
              ? null
              : prev
          );
    } catch (err) {
          console.error(err);
          setError(`Failed to save: ${getVehicleReportGridErrorMessage(err)}`);
        }
      }, 600);
    },
    [syncPeriodInStore]
  );

  const updateRow = useCallback(
    (id, patch) => {
      setRows((prev) => {
        const current = prev.find((r) => r.id === id);
        if (!current) return prev;

        const updated = withDerived({ ...current, ...patch });
        const next = prev.map((r) => (r.id === id ? updated : r));
        rowSnapshotsRef.current[id] = updated;
        scheduleSave(id);
        return next;
      });
    },
    [scheduleSave]
  );

  const clearFilters = () => {
    setFilterProject('All');
    setFilterCompany('All');
    setSearchQuery('');
  };

  const applyExportFilters = useCallback(
    (dataRows) =>
      dataRows.filter((r) => {
        if (filterProject !== 'All' && r.project !== filterProject) return false;
        if (filterCompany !== 'All' && r.rentalCompany !== filterCompany) return false;
        if (!rowMatchesSearch(r, searchQuery)) return false;
        return true;
      }),
    [filterProject, filterCompany, searchQuery]
  );

  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    for (let month = 1; month <= 12; month += 1) {
      const monthRows = sortPeriodRows(
        applyExportFilters(
          allRowsRef.current.filter((r) => r.year === selectedYear && r.month === month)
        )
      );
      const monthStats = computeVehicleReportStats(monthRows);
      const sheetRows = monthRows.map((r) => ({
        Project: r.project,
        'User Name': r.userName,
        'Reg Number': r.regNumber,
        'Rental Company': r.rentalCompany,
        'Previous Odometer (KM)': Number(r.previousOdometerKm) || 0,
        'Current Odometer (KM)': Number(r.currentOdometerKm) || 0,
        'KM Traveled': Number(r.kmMoved) || 0,
        'Fuel Used (L)': Number(r.fuelUsedLiters) || 0,
        'Fuel Cost by Card': Number(r.fuelCostCard) || 0,
        'Fuel Cost by Cash': Number(r.fuelCostCash) || 0,
        'Total Fuel Cost': Number(r.totalFuelCost) || 0,
        'Rental Days': Number(r.rentalDays) || 0,
        'Price per Month': Number(r.pricePerMonth) || 0,
        'Fuel Consumption (KM/L)': Number(r.fuelKmPerLiter) || 0,
        'Liter per 100 KM': Number(r.literPer100Km) || 0
      }));
      sheetRows.push({
        Project: '',
        'User Name': '',
        'Reg Number': '',
        'Rental Company': 'TOTAL',
        'Previous Odometer (KM)': '',
        'Current Odometer (KM)': '',
        'KM Traveled': monthStats.totalKm,
        'Fuel Used (L)': monthStats.totalFuelL,
        'Fuel Cost by Card': '',
        'Fuel Cost by Cash': '',
        'Total Fuel Cost': monthStats.totalFuelCost,
        'Rental Days': '',
        'Price per Month': '',
        'Fuel Consumption (KM/L)': monthStats.avgKmPerLiter,
        'Liter per 100 KM': ''
      });
      const ws = XLSX.utils.json_to_sheet(sheetRows);
      XLSX.utils.book_append_sheet(wb, ws, MONTH_SHORT[month - 1]);
    }
    XLSX.writeFile(wb, `Vehicle_Report_${selectedYear}.xlsx`);
  };

  const periodMonthLabel = MONTH_LABELS[selectedMonth - 1] || '';

  const clearTableHover = () => {
    setHoverRow(null);
    setHoverCol(null);
  };

  const vrTdProps = (rowIndex, colIndex) => ({
    'data-vr-row': rowIndex,
    'data-vr-col': colIndex,
    onMouseEnter: () => {
      setHoverRow(rowIndex);
      setHoverCol(colIndex);
    }
  });

  const renderNumericCell = (row, field, isInt = false, decimals = 2) => {
    const focusKey = `${field}-${row.id}`;
    const isFocused = amountFocusId === focusKey;
    const value = row[field];
    return (
      <input
        className="vr-cell-input vr-cell-amount"
        value={
          isFocused
            ? String(value === 0 ? '' : value)
            : isInt
              ? String(value || 0)
              : formatNumber(value, decimals)
        }
        onFocus={() => setAmountFocusId(focusKey)}
        onChange={(e) =>
          updateRow(row.id, {
            [field]: isInt ? parseIntInput(e.target.value) : parseAmountInput(e.target.value),
          })
        }
        onBlur={() => setAmountFocusId(null)}
      />
    );
  };

  const summaryCards = [
    {
      id: 'km',
      label: 'Total KM traveled',
      value: formatNumber(stats.totalKm, 0),
      accent: '#3b82f6',
      icon: Route,
      meta: filtersActive ? 'Filtered' : 'This month',
    },
    {
      id: 'fuel',
      label: 'Total fuel (L)',
      value: formatNumber(stats.totalFuelL),
      accent: '#10b981',
      icon: Droplets,
      meta: filtersActive ? 'Filtered' : 'This month',
    },
    {
      id: 'cost',
      label: 'Total fuel cost',
      value: formatAmount(stats.totalFuelCost),
      accent: '#f59e0b',
      icon: Fuel,
      meta: filtersActive ? 'Filtered' : 'This month',
    },
    {
      id: 'avg',
      label: 'Avg KM/L',
      value: formatNumber(stats.avgKmPerLiter),
      accent: '#8b5cf6',
      icon: Gauge,
      meta: filtersActive ? 'Filtered' : 'This month',
    },
  ];

  return (
    <div className="vr-page">
      <div className="vr-scroll">
        <div className="vr-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Fleet', to: '/sagemcom/fleet-management', hideOnMobile: true },
              { label: 'Vehicle report' },
            ]}
          />

          {loading && rows.length === 0 && !error ? (
            <VrPageSkeleton />
          ) : (
            <>
              <section className="vr-intro" aria-label="Vehicle report overview">
                <div className="vr-intro-text">
                  <h1 className="vr-intro-title">Vehicle report</h1>
                  <p className="vr-intro-subtitle">
                    Monthly fuel and mileage — rows sync from Car Rental Invoicing
                  </p>
            </div>
                <div className="vr-intro-actions">
                  <select
                    id="vr-year"
                    className="vr-period-select"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    aria-label="Year"
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                  <select
                    id="vr-month"
                    className="vr-period-select"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    aria-label="Month"
                  >
                    {MONTH_LABELS.map((label, i) => (
                      <option key={label} value={i + 1}>
                        {MONTH_SHORT[i]}
                      </option>
                    ))}
                  </select>
                  <Button
                type="button"
                    variant="ghost"
                    size="icon"
                    className="vr-soft-btn h-9 w-9"
                    onClick={handleExport}
                    disabled={loading || refreshing}
                    title="Download Excel"
                    aria-label="Download Excel"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
            </div>
              </section>

              {error ? (
                <div className="vr-error" role="alert">
                  {error}
                </div>
              ) : null}

              <div className="vr-summary-cards">
                {summaryCards.map((card) => {
                  const Icon = card.icon;
                          return (
                    <div
                      key={card.id}
                      className="vr-summary-card"
                      style={{ '--cat-accent': card.accent }}
                    >
                      <div className="vr-summary-card__top">
                        <span className="vr-summary-card__icon" aria-hidden>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="vr-summary-card__label">{card.label}</span>
                      </div>
                      <div className="vr-summary-card__count">{card.meta}</div>
                      <div className="vr-summary-card__amount">{card.value}</div>
                    </div>
                  );
                })}
                </div>

              <div className="vr-table-section__head">
                <h2>
                  {periodMonthLabel} {selectedYear}
                  <span className="vr-table-section__count">{totalFiltered}</span>
                  {syncing ? (
                    <span className="vr-sync-hint">Syncing from Car Rental…</span>
                  ) : null}
                  {refreshing && !syncing ? (
                    <span className="vr-sync-hint">Updating…</span>
                  ) : null}
                </h2>
                {duplicateRegCount > 0 ? (
                  <span className="vr-duplicate-badge" role="status">
                    {duplicateRegCount} duplicate reg{' '}
                    {duplicateRegCount === 1 ? 'number' : 'numbers'}
                  </span>
                ) : null}
              </div>

              <div className="vr-toolbar">
                <div className="vr-search">
                  <Search className="vr-search__icon h-4 w-4" aria-hidden />
                  <Input
                    className="vr-search__input"
                    placeholder="Search project, user, reg, company…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search vehicle entries"
                />
              </div>
                <select
                  className="vr-filter-select"
                  value={filterProject}
                  onChange={(e) => setFilterProject(e.target.value)}
                  aria-label="Project filter"
                >
                  {projectOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === 'All' ? 'All projects' : opt}
                    </option>
                  ))}
                </select>
                <select
                  className="vr-filter-select"
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  aria-label="Company filter"
                >
                  {companyOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === 'All' ? 'All companies' : opt}
                    </option>
                  ))}
                </select>
                <div className="vr-toolbar__spacer" aria-hidden="true" />
                <Button
                  type="button"
                  variant="ghost"
                  className="vr-soft-btn h-9 gap-1.5"
                  onClick={clearFilters}
                  disabled={!filtersActive}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Clear
                </Button>
            </div>

              <div className="vr-table-section">
                <div className={`vr-table-shell${refreshing ? ' is-refreshing' : ''}`}>
                  <div
                    className="vr-table-wrapper"
                    onMouseLeave={clearTableHover}
                  >
                    {(hoverRow !== null || hoverCol !== null) && (
                      <style>{`
                        ${
                          hoverRow !== null
                            ? `
                        .vr-table tbody td[data-vr-row="${hoverRow}"] {
                          background-color: #f8fafc !important;
                        }
                        .vr-table tbody td[data-vr-row="${hoverRow}"] .vr-cell-input {
                          background-color: #f8fafc !important;
                        }
                        `
                            : ''
                        }
                        ${
                          hoverCol !== null
                            ? `
                        .vr-table tbody td[data-vr-col="${hoverCol}"],
                        .vr-table thead th[data-vr-col="${hoverCol}"] {
                          background-color: #f8fafc !important;
                        }
                        `
                            : ''
                        }
                        ${
                          hoverRow !== null && hoverCol !== null
                            ? `
                        .vr-table tbody td[data-vr-row="${hoverRow}"][data-vr-col="${hoverCol}"] {
                          background-color: #f1f5f9 !important;
                        }
                        .vr-table tbody td[data-vr-row="${hoverRow}"][data-vr-col="${hoverCol}"] .vr-cell-input {
                          background-color: #f1f5f9 !important;
                        }
                        `
                            : ''
                        }
                        .dark .vr-table tbody td[data-vr-row="${hoverRow}"],
                        .dark .vr-table tbody td[data-vr-col="${hoverCol}"],
                        .dark .vr-table thead th[data-vr-col="${hoverCol}"] {
                          background-color: rgba(255, 255, 255, 0.04) !important;
                        }
                        .dark .vr-table tbody td[data-vr-row="${hoverRow}"] .vr-cell-input,
                        .dark .vr-table tbody td[data-vr-col="${hoverCol}"] .vr-cell-input {
                          background-color: transparent !important;
                        }
                      `}</style>
                    )}
                    <table className="vr-table">
                      <thead>
                        <tr>
                          {VR_TABLE_HEADERS.map((label, colIndex) => (
                            <th key={label} data-vr-col={colIndex}>
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRows.length === 0 ? (
                          <tr>
                            <td colSpan={15} className="vr-empty-row">
                              No vehicles for this month — add entries in Car Rental Invoicing
                              first.
                            </td>
                          </tr>
                        ) : (
                          paginatedRows.map((row, rowIndex) => {
                            const isRegDuplicate = duplicateRegRowIds.has(row.id);
                          return (
                              <tr
                                key={row.id}
                                className={
                                  isRegDuplicate ? 'vr-row-reg-duplicate' : undefined
                                }
                              >
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 0)}>
                <input
                                    className="vr-cell-input vr-cell-readonly"
                                    readOnly
                                    tabIndex={-1}
                                    value={row.project}
                                  />
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 1)}>
                <input
                                    className="vr-cell-input vr-cell-readonly"
                                    readOnly
                                    tabIndex={-1}
                                    value={row.userName}
                                  />
                                </td>
                                <td
                                  className={`vr-td-data${isRegDuplicate ? ' vr-td-reg--duplicate' : ''}`}
                                  {...vrTdProps(rowIndex, 2)}
                                >
                <input
                                    className={`vr-cell-input vr-cell-readonly${isRegDuplicate ? ' vr-cell-input--duplicate' : ''}`}
                                    readOnly
                                    tabIndex={-1}
                                    value={row.regNumber}
                                    title={
                                      isRegDuplicate
                                        ? 'Duplicate registration number'
                                        : 'From Car Rental Invoicing'
                                    }
                                  />
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 3)}>
                  <input
                                    className="vr-cell-input vr-cell-readonly"
                                    readOnly
                                    tabIndex={-1}
                                    value={row.rentalCompany}
                                  />
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 4)}>
                                  {renderNumericCell(row, 'previousOdometerKm', false, 0)}
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 5)}>
                                  {renderNumericCell(row, 'currentOdometerKm', false, 0)}
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 6)}>
                  <input
                                    className="vr-cell-input vr-cell-readonly"
                                    readOnly
                                    tabIndex={-1}
                                    value={formatNumber(row.kmMoved, 0)}
                                    title="Current odometer − previous odometer"
                                  />
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 7)}>
                                  {renderNumericCell(row, 'fuelUsedLiters')}
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 8)}>
                                  {renderNumericCell(row, 'fuelCostCard')}
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 9)}>
                                  {renderNumericCell(row, 'fuelCostCash')}
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 10)}>
                  <input
                                    className="vr-cell-input vr-cell-readonly"
                                    readOnly
                                    tabIndex={-1}
                                    value={formatAmount(row.totalFuelCost)}
                                    title="Card + cash"
                                  />
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 11)}>
                                  {renderNumericCell(row, 'rentalDays', true)}
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 12)}>
                                  {renderNumericCell(row, 'pricePerMonth')}
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 13)}>
                <input
                                    className="vr-cell-input vr-cell-readonly"
                                    readOnly
                                    tabIndex={-1}
                                    value={formatNumber(row.fuelKmPerLiter)}
                                    title="KM traveled ÷ fuel used (L)"
                                  />
                                </td>
                                <td className="vr-td-data" {...vrTdProps(rowIndex, 14)}>
                <input
                                    className="vr-cell-input vr-cell-readonly"
                                    readOnly
                                    tabIndex={-1}
                                    value={formatNumber(row.literPer100Km)}
                                    title="(Fuel used ÷ KM traveled) × 100"
                                  />
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                      {paginatedRows.length > 0 ? (
                        <tfoot>
                          <tr>
                            <td colSpan={4} className="vr-tfoot-total">
                              Totals (filtered)
                            </td>
                            <td colSpan={2} />
                            <td className="vr-tfoot-total">
                              {formatNumber(stats.totalKm, 0)}
                            </td>
                            <td className="vr-tfoot-total">
                              {formatNumber(stats.totalFuelL)}
                            </td>
                            <td colSpan={2} />
                            <td className="vr-tfoot-total">
                              {formatAmount(stats.totalFuelCost)}
                            </td>
                            <td colSpan={2} />
                            <td className="vr-tfoot-total">
                              {formatNumber(stats.avgKmPerLiter)}
                            </td>
                            <td />
                          </tr>
                        </tfoot>
                      ) : null}
                    </table>
              </div>
            </div>

                {totalFiltered > 0 ? (
                  <div className="vr-pagination">
                    <div className="vr-pagination__info">
                      Showing {indexOfFirstItem}–{indexOfLastItem} of {totalFiltered}
                    </div>
                    <div className="vr-pagination__controls">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="vr-soft-btn h-8 w-8"
                        onClick={goToPreviousPage}
                        disabled={safePage === 1}
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="vr-pagination__numbers">
                        {getPageNumbers().map((pageNum, index) =>
                          pageNum === '...' ? (
                            <span
                              key={`ellipsis-${index}`}
                              className="vr-pagination__ellipsis"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={pageNum}
                              type="button"
                              className={`vr-pagination__num${safePage === pageNum ? ' is-active' : ''}`}
                              onClick={() => goToPage(pageNum)}
                            >
                              {pageNum}
                </button>
                          )
              )}
            </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="vr-soft-btn h-8 w-8"
                        onClick={goToNextPage}
                        disabled={safePage === totalPages}
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
            </div>
        </div>
                ) : null}
        </div>
            </>
            )}
          </div>
      </div>
    </div>
  );
}

export default VehicleReport;
